import { useMemo, useState } from "react";

import noop from "lodash/noop";

import { initialDataToSave } from "app/pages/activation/modules/Settings/config";
import type { DataToSaveSchema } from "app/pages/activation/modules/Settings/types";
import { SettingsTabs } from "app/pages/activation/modules/Settings/types";
import useSaveSettingsMutation from "app/pages/activation/queries/settings/useSaveSettingsMutation";
import { ACTIVATION_PROJECT_ROUTE, DEFAULT_ACTIVATION_GRANULARITY } from "app/pages/activation/routing/constants";
import { activationTracking } from "app/pages/activation/services/tracking";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { useActivationGaMappingMutation, useActivationGaMappingState } from "../../components/ga-connect";
import { ActivationComponentAssets } from "../../helpers/ActivationAssets";

type SettingsSchema = {
    activationId: string;
    isChanged: boolean;
    currentTab: SettingsTabs;
};

export const useSaveSettings = ({ activationId, currentTab, isChanged }: SettingsSchema) => {
    const { mutateAsync: saveSettings, isPending: isSaveLoading } = useSaveSettingsMutation(activationId);
    const { hasGaMapping, isMappingLoading } = useActivationGaMappingState(activationId, {
        shouldIgnoreResourceNotFoundError: true,
    });
    const { mutateAsync: updateGaMapping, isPending: isGaMappingUpdatePending } = useActivationGaMappingMutation({
        activationId,
        hasGaMapping,
    });
    const apiParams = proNavigator.getApiParams();

    const [
        {
            activationSetName,
            competitors,
            gaMapping,
            isGeneralSettingsChanged,
            nameError,
            onSaveNewsSettings,
            onSubscribe,
            selectedCountry,
            selectedWebsite,
            trafficSource,
        },
        setDataToSave,
    ] = useState<DataToSaveSchema>(initialDataToSave);

    const maxCompetitors = ActivationComponentAssets.competitorsLimit;

    const onSave = (withHomeRedirect = true) => {
        activationTracking.settings.trackSave();
        const goHome = withHomeRedirect
            ? (id?: string, country?: number) =>
                  proNavigator.go(ACTIVATION_PROJECT_ROUTE, {
                      key: id || activationId,
                      country: country || apiParams.country,
                      webSource: apiParams.webSource || "Total",
                      timeGranularity: apiParams?.timeGranularity || DEFAULT_ACTIVATION_GRANULARITY,
                  })
            : noop;

        if (currentTab === SettingsTabs.Subscriptions) {
            onSubscribe();
            goHome();
        } else if (currentTab === SettingsTabs.NewsSettings) {
            void onSaveNewsSettings()
                .then(() => goHome())
                // Validation error blocks redirect; API errors surface via toast inside NewsSettings mutation
                .catch(noop);
        } else {
            const mutations: Promise<unknown>[] = [];

            if (isGeneralSettingsChanged) {
                mutations.push(
                    saveSettings(
                        {
                            id: activationId,
                            country: selectedCountry,
                            domain: selectedWebsite?.name,
                            competitorsDomains: competitors.competitors.map(({ name }) => name),
                            name: activationSetName,
                            trafficSource,
                        },
                        {
                            onSuccess: (result) => {
                                activationTracking.settings.trackUpdate({
                                    competitive_set_id: result?.id,
                                    country: result?.country,
                                    main_website: result?.primaryDomain,
                                    competitors: result.competitorsDomains.join(","),
                                    name: activationSetName,
                                });
                            },
                        },
                    ),
                );
            }

            if (gaMapping?.isChanged && (hasGaMapping || gaMapping.isActive)) {
                mutations.push(
                    updateGaMapping([
                        activationId,
                        {
                            connectionId: gaMapping.connectionId,
                            isActive: gaMapping.isActive,
                            propertyId: gaMapping.propertyId,
                            streamId: gaMapping.streamId,
                        },
                    ]),
                );
            }

            void Promise.all(mutations).then(() => {
                goHome(activationId, selectedCountry);
            });
        }
    };

    const isSaveDisabled = useMemo(() => {
        if (!isChanged || isMappingLoading) {
            return true;
        }

        return currentTab === SettingsTabs.General
            ? !(
                  selectedWebsite &&
                  selectedCountry &&
                  !!competitors.competitors.length &&
                  competitors.competitors.length <= maxCompetitors &&
                  !nameError.isError
              )
            : false;
    }, [currentTab, isChanged, selectedWebsite, selectedCountry, competitors, nameError, isMappingLoading]);

    return {
        isSaveDisabled,
        onSave,
        isSaveLoading: isSaveLoading || isGaMappingUpdatePending,
        setDataToSave,
        competitors: competitors.competitors,
    };
};
